/* eslint-disable no-param-reassign */
import React from 'react';
import config from '../../../config';
import styles from './link-list.module.scss';
import { ReactComponent as ChevronIcon } from '../../../assets/svg/chevron.svg';
import sanitizeId from '../../../utils/sanitizeId';
import { LinkListViewProps } from '../types';

const LinkListView = ({
  activeSubMenu,
  firstLinkRef,
  handleKeyDown,
  isMenuActive = false,
  linkRefs,
  linkSection,
  onClickLink,
  setActiveSubMenu,
}: LinkListViewProps) => (
  <div
    aria-hidden={!(isMenuActive && activeSubMenu === linkSection.name)}
    aria-labelledby={`${sanitizeId(linkSection.name)}-sub-menu`}
    className={`${styles.links} ${activeSubMenu === linkSection.name ? styles.links__open : ''}`}
    id={`${sanitizeId(linkSection.name)}-sub-menu-panel`}
    role="group"
  >
    <button
      className={styles['links-button']}
      onClick={() => setActiveSubMenu(undefined)}
      type="button"
      aria-label={`Close ${linkSection.name} links and return to sub-menu`}
      tabIndex={isMenuActive && activeSubMenu === linkSection.name ? 0 : -1}
    >
      <ChevronIcon aria-hidden />
      {linkSection.name}
    </button>
    <ul
      className={`${styles['links-list']} ${activeSubMenu === linkSection.name ? styles['links-list__active'] : ''}`}
      id={`${sanitizeId(linkSection.name)}-link-list`}
      data-allow-touch-move="true"
      role="menu"
      aria-label={`${linkSection.name} links`}
    >
      {linkSection.links.map((link, index) => {
        const path = link.path.startsWith('/') ? link.path : `/${link.path}`;
        const fullUrl = `${config.Z3_URL.replace(/\/$/, '')}${path}`;

        return (
          <li
            className={styles['links-item']}
            key={link.id || link.name}
            role="none"
          >
            <a
              aria-describedby={index === 0 ? `${sanitizeId(linkSection.name)}-link-instructions` : undefined}
              className={styles['links-link']}
              href={fullUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => onClickLink(e, link)}
              onKeyDown={(e) => handleKeyDown(e, link, index)}
              ref={(el) => {
                const linkId = link.id || link.name;
                linkRefs.current[linkId] = el;
                if (index === 0) firstLinkRef.current = el;
              }}
              role="menuitem"
              tabIndex={isMenuActive && activeSubMenu === linkSection.name && index === 0 ? 0 : -1}
            >
              {link.name}
            </a>
          </li>
        );
      })}
    </ul>
    {/* Hidden instructions for screen readers */}
    <span
      id={`${sanitizeId(linkSection.name)}-link-instructions`}
      className="sr-only"
      aria-live="polite"
    >
      Use arrow keys to navigate links, Arrow Left to return to sub-menu, Escape to close
    </span>
    {linkSection.image && linkSection.image.uri && (
      <img
        alt={linkSection.image.alt ?? ''}
        className={styles.links__image}
        src={linkSection.image.uri}
        height={590}
        width={414}
        loading="lazy"
      />
    )}
  </div>
);

export default LinkListView;
